/// <reference path="../../../monaco-typescript/src/monaco.d.ts" />
import { ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TSESTree } from '@typescript-eslint/types';
import { connectState, getTrackByIndexFn, InputObservable } from '@y42/angular/util';
import { addPrettierFormatting, monacoColorThemes, MonacoEditorLoader } from '@y42/monaco-editor';
import { TypescriptTypeEnum } from '@y42/monaco-typescript-types';
import { assertNonNullable } from '@y42/util/assert';
import { BehaviorSubject, combineLatest, debounceTime, filter, map, Observable, switchMap, tap } from 'rxjs';
import { getFunctionDefinitionString } from './helpers/function-definition-string';
import { typeDeclarations } from './helpers/type-declarations';
import { FunctionDefinitionModel } from './models/function-definition.model';
import { MonacoContentfulFunctionsState } from './monaco-contentful-functions.state';

interface ParserTypescriptModule {
  parsers: {
    typescript: {
      parse: (arg: unknown) => TSESTree.Program;
    };
  };
}

type RenderElementFunction = (
  element: { completion: { cssClass?: string } },
  index: unknown,
  data: { iconContainer: { classList: { add: (paramOne: string, paramTwo: string) => void } } },
) => unknown;

interface SuggestWidgetRendererModule {
  ItemRenderer: {
    prototype: {
      renderElement: RenderElementFunction & {
        patched?: boolean;
      };
    };
  };
}

declare const window: {
  require: (paths: string[], cb: (module: ParserTypescriptModule | SuggestWidgetRendererModule) => void) => void;
};

@UntilDestroy()
@Component({
  selector: 'y42-function-editor',
  templateUrl: './function-editor.component.html',
  styleUrls: ['./function-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FunctionEditorComponent implements OnDestroy {
  constructor(
    private readonly zone: NgZone,
    private readonly monacoContentfulFunctionsState: MonacoContentfulFunctionsState,
    protected readonly monacoLoader: MonacoEditorLoader,
  ) {
    this.addEditorLibsOnConfigChange();
    this.setEditorValueOnInputCodeChange();
    this.revalidateOnAllowedOutputTypesChangeListener();
  }

  readonly trackByIndex = getTrackByIndexFn<monaco.editor.IMarker>();

  private readonly showAggregateFunctions$$ = new BehaviorSubject(false);

  @Input()
  @InputObservable()
  inputCode!: string;
  private readonly inputCode$!: Observable<string | undefined>;

  @Input()
  @InputObservable()
  sourceColumns!: string;
  private readonly sourceColumns$!: Observable<string | undefined>;

  @Input()
  @InputObservable()
  allowedOutputTypes!: TypescriptTypeEnum[]; // what are the types that the monaco editor accepts
  private readonly allowedOutputTypes$!: Observable<TypescriptTypeEnum[] | undefined>;

  @Input() showDefaultInfo = true; // whether to show some explanation on the bottom
  @Input() errorInfoHeight = '150px'; // to show the error box on the bottom

  @Input() set showAggregateFunctions(show: boolean) {
    this.showAggregateFunctions$$.next(show);
  }

  @Output() validity: EventEmitter<boolean> = new EventEmitter(); // checks whether the values from the editor is valid
  @Output() functionNameClicked: EventEmitter<string> = new EventEmitter(); // when we click on a function name
  @Output() variableClicked: EventEmitter<string> = new EventEmitter(); // when we click on a column and
  @Output() focusedIndex: EventEmitter<void> = new EventEmitter(); // in case of multiple monaco editors, we know which on the user is focussing on
  @Output() editorValueChanged: EventEmitter<string> = new EventEmitter();

  functionDefs$: Observable<FunctionDefinitionModel[]> = this.showAggregateFunctions$$.pipe(
    switchMap((show) => this.monacoContentfulFunctionsState.selectMonacoFunctions(show)),
  );

  private readonly monacoErrors$$ = new BehaviorSubject<monaco.editor.IMarker[]>([]);

  onMarkerChange?: { dispose: () => void };

  functionsWithSameParamType: string[] = [];

  private readonly editor$$ = new BehaviorSubject<monaco.editor.IStandaloneCodeEditor | undefined>(undefined);

  readonly editorOptions = {
    language: 'typescript',
    theme: monacoColorThemes.vs.light,
    suggest: {
      showKeywords: false,
    },
  };

  private get editorInstance() {
    return this.editor$$.value;
  }

  readonly state = connectState(this, {
    editorInitialized: this.editor$$.pipe(map((editor) => !!editor)),
    monacoErrors: this.monacoErrors$$,
  });

  public get monacoErrors() {
    return this.monacoErrors$$.value;
  }

  async initEditor(editor: monaco.editor.IStandaloneCodeEditor) {
    this.configureMonaco();

    this.editor$$.next(editor);
    this.editorInstance?.createContextKey('editorTabMovesFocus', true);

    this.setupCustomDecorationsAndValidations();

    const standaloneServicesModule = await this.monacoLoader.retrieveStandaloneServicesModule();

    this.onMarkerChange?.dispose();
    this.onMarkerChange = standaloneServicesModule.StaticServices.markerService.get().onMarkerChanged(() => {
      // Updated Markers
      this.zone.run(() => this.updateValidity());
    });

    if (this.editorInstance) {
      addPrettierFormatting(this.editorInstance, [`/assets/prettier/parser-typescript.js`]);
    }
    this.configureControlClick();
  }

  public useFunction(selectedFunction: string) {
    if (selectedFunction) {
      this.editorInstance?.focus();
      const operation = {
        identifier: { major: 1, minor: 1 },
        range: this.editorInstance?.getSelection(),
        text: `${selectedFunction}(@)`,
        forceMoveMarkers: true,
      } as monaco.editor.IIdentifiedSingleEditOperation;
      this.editorInstance?.executeEdits(
        '',
        [operation],
        (inverseEditOperations: monaco.editor.IValidEditOperation[]) => {
          const inverseEditOperation = inverseEditOperations[0];
          assertNonNullable(inverseEditOperation);
          const { startLineNumber, endColumn } = inverseEditOperation.range;
          return [new monaco.Selection(startLineNumber, endColumn - 1, startLineNumber, endColumn - 1)];
        },
      );
      this.editorInstance?.trigger('keyboard', 'editor.action.triggerParameterHints', null);
    }
  }

  ngOnDestroy(): void {
    this.editorInstance?.dispose();
    this.onMarkerChange?.dispose();

    this.showAggregateFunctions$$.complete();
  }

  private updateValidity() {
    const model = this.editorInstance?.getModel();
    const monacoErrors = monaco.editor.getModelMarkers({
      resource: model?.uri,
    });
    this.monacoErrors$$.next(monacoErrors);
    this.validity.emit(monacoErrors.length === 0 && !!model?.getValue());
  }

  private configureControlClick() {
    this.editorInstance?.onMouseDown(({ target, event }: monaco.editor.IEditorMouseEvent) => {
      const isTextClicked =
        (event.ctrlKey || event.metaKey) &&
        target.type === monaco.editor.MouseTargetType.CONTENT_TEXT &&
        target.element;
      if (isTextClicked) {
        if (target?.element?.classList?.contains('token-fn')) {
          event.preventDefault();
          this.functionNameClicked.emit(target?.element?.textContent || undefined);
        }
        if (target?.element?.classList?.contains('token-var')) {
          event.preventDefault();
          this.variableClicked.emit(target?.element?.textContent || undefined);
        }
      }

      this.focusedIndex.emit();
    });

    this.editorInstance?.onKeyDown((e: monaco.IKeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        this.editorInstance?.getDomNode()?.classList.add('highlight-fn');
      }
    });
    this.editorInstance?.onKeyUp((e: monaco.IKeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        this.editorInstance?.getDomNode()?.classList.remove('highlight-fn');
      }
    });
  }

  private configureMonaco(): void {
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({ noLib: true });
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      noLib: true,
      allowNonTsExtensions: true,
    });

    monaco.languages.typescript.typescriptDefaults.addExtraLib(typeDeclarations, 'typeDeclarations');

    // allow to provide custom icon class for completion item
    window.require(['vs/editor/contrib/suggest/suggestWidgetRenderer'], (m) => {
      const renderer = m as SuggestWidgetRendererModule;
      const originRender = renderer.ItemRenderer.prototype.renderElement;
      if (
        (
          originRender as {
            patched: boolean;
          }
        ).patched
      ) {
        return;
      }
      renderer.ItemRenderer.prototype.renderElement = function (
        element: { completion: { cssClass?: string } },
        index: unknown,
        data: { iconContainer: { classList: { add: (paramOne: string, paramTwo: string) => void } } },
      ) {
        type Args = Parameters<typeof originRender>;
        const result = (originRender as RenderElementFunction).apply(this, arguments as unknown as Args);
        const { completion } = element;
        if (completion.cssClass) {
          data.iconContainer.classList.add('monaco-token-type', `monaco-token-type--${completion.cssClass}`);
        }
        return result;
      };
      renderer.ItemRenderer.prototype.renderElement.patched = true;
    });
  }

  private setupCustomDecorationsAndValidations() {
    this.editorInstance?.onDidChangeModelContent(() => {
      const code = this.editorInstance?.getValue();
      this.editorValueChanged.emit(code);
      this.updateDecorationsAndCustomErrors();
    });

    this.editorInstance?.onDidBlurEditorWidget(() => {
      this.editorInstance?.trigger('keyboard', 'closeParameterHints', null);
    });

    this.updateDecorationsAndCustomErrors();
  }

  private async updateDecorationsAndCustomErrors() {
    const worker = await monaco.languages.typescript.getTypeScriptWorker();

    const model = this.editorInstance?.getModel();
    const modelUri = model?.uri;
    if (!modelUri) {
      return;
    }

    type TypeScriptWorkerWithGetDecorationsAndCustomErrors = monaco.languages.typescript.TypeScriptWorker & {
      getDecorationsAndCustomErrors: (
        fileName: string,
        options: { allowedOutputTypes: string[]; functionsWithSameParamType: string[] },
      ) => Promise<{
        decorations: monaco.editor.IModelDeltaDecoration[];
        customErrors: monaco.languages.typescript.Diagnostic[];
      }>;
    };

    const workerInstance = await worker(modelUri);
    const workerInstanceCasted = workerInstance as TypeScriptWorkerWithGetDecorationsAndCustomErrors;

    const { customErrors } = await workerInstanceCasted.getDecorationsAndCustomErrors(modelUri.toString(), {
      allowedOutputTypes: this.allowedOutputTypes,
      functionsWithSameParamType: this.functionsWithSameParamType,
    });

    const errorMarkers = customErrors.map((diag: monaco.languages.typescript.Diagnostic) => {
      const diagStart = diag.start || 0;
      const diagLength = diag.length || 1;
      const { lineNumber: startLineNumber, column: startColumn } = model.getPositionAt(diagStart);
      const { lineNumber: endLineNumber, column: endColumn } = model.getPositionAt(diagStart + diagLength);
      return {
        severity: monaco.MarkerSeverity.Error,
        startLineNumber,
        startColumn,
        endLineNumber,
        endColumn,
        message: `${diag.messageText}`,
      };
    });
    this.updateValidity();
    monaco.editor.setModelMarkers(model, '', errorMarkers);
  }

  private addEditorLibsOnConfigChange() {
    this.editor$$
      .pipe(
        filter((initialized) => !!initialized),
        switchMap(() =>
          combineLatest([this.functionDefs$, this.sourceColumns$]).pipe(
            tap(([functionDefs, sourceColumns]) => {
              monaco.languages.typescript.typescriptDefaults.addExtraLib(sourceColumns || '', 'sourceColumns');
              monaco.languages.typescript.typescriptDefaults.addExtraLib(
                getFunctionDefinitionString(functionDefs) || '',
                'functionDefinitions',
              );

              this.functionsWithSameParamType = functionDefs
                .filter((fn) => fn.requires_same_parameter_type)
                .map((fn) => fn.function_name.toLowerCase());
            }),
            debounceTime(100), // TODO: we asume that after 100ms data to worker will be propagated
            tap(() => this.updateDecorationsAndCustomErrors()),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private setEditorValueOnInputCodeChange() {
    combineLatest([this.editor$$, this.inputCode$])
      .pipe(
        filter(([editor, __]) => !!editor),
        tap(([__, inputCode]) => {
          const newValue = inputCode || '';
          if (newValue !== this.editorInstance?.getValue()) {
            this.editorInstance?.setValue(newValue);
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private revalidateOnAllowedOutputTypesChangeListener() {
    // we want to revalidate if content/model is valid when allowedOutputTypes changes
    combineLatest([this.editor$$, this.allowedOutputTypes$])
      .pipe(
        filter(([editor, __]) => !!editor),
        switchMap(() => this.updateDecorationsAndCustomErrors()),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
